import { buttonOf, drawSavedWord } from "./save-word-control.js";
import type { YueduWordRows } from "./yuedu-word-rows.js";
import { holdRows } from "./yuedu-stage.js";

/**
 * A signed-in reader's 生词 on a passage: the words in it they have not saved.
 *
 * Saving is how the site knows a reader has met a word, so a word they have
 * not saved is one to mark (Hugh, 2026-10-23). This holds which words the
 * reader holds, marks the others in the text and lists them under it, and
 * redraws all three when a press anywhere on the page saves or removes one.
 *
 * **The stylesheet does the showing.** A marked word carries `yuedu-shengci`,
 * which tints it and keeps its reading, and the passage carries
 * `data-cb-shengci` once the marks are on. Until then `modules/yuedu` hides
 * the reading over every word a signed-in reader could hold, so the readings
 * arrive over the 生词 rather than every one of them showing and then going.
 *
 * **A word saved from the list keeps its row**, its button saying Saved, so
 * nothing moves under the reader's pointer. It is gone from the list the next
 * time the page is drawn.
 *
 * **A staged passage holds back rows** of words first used in lines it has
 * not shown yet (CB-77). `./yuedu-stage.ts` marks them once the list is drawn.
 */

/** A word in the text, carrying its 简体 on `data-cb-word`. */
const wordSelector = "[data-cb-word]";

/** The class a word not saved carries, in the text and on its row. */
const shengciClass = "yuedu-shengci";

export class YueduShengci {
  private readonly passages: readonly HTMLElement[];
  private readonly section: HTMLElement | null;
  private readonly list: HTMLElement | null;
  private readonly some: HTMLElement | null;
  private readonly none: HTMLElement | null;
  private readonly rows: YueduWordRows;
  private readonly held = new Set<string>();

  constructor(properties: {
    /**
     * The parts of the page holding words to mark: the passage's text, a link
     * per word, and the questions on it.
     */
    readonly passages: readonly HTMLElement[];

    /**
     * The section the list is in, `hidden` until the marks are on, or nothing
     * where the page lists none.
     */
    readonly section: HTMLElement | null;

    readonly rows: YueduWordRows;
  }) {
    this.passages = properties.passages;
    this.section = properties.section;
    this.rows = properties.rows;
    this.list =
      this.section?.querySelector<HTMLElement>("[data-cb-yuedu-list]") ??
      this.section;
    this.some = this.section?.querySelector("[data-cb-yuedu-some]") ?? null;
    this.none = this.section?.querySelector("[data-cb-yuedu-none]") ?? null;
  }

  /**
   * Mark the words the reader does not hold and list them.
   * @param held - The words the reader holds, as the route answered.
   * @returns The list, for the caller to bind the save controls in, or
   *   nothing where the page lists none.
   */
  start(held: readonly string[]): HTMLElement | null {
    for (const word of held) {
      this.held.add(word);
    }

    const shengci = this.rows.words().filter((word) => !this.held.has(word));

    this.list?.replaceChildren(
      ...shengci.flatMap((word) => {
        const row = this.rows.row(word);

        return row === undefined ? [] : [row];
      }),
    );

    if (this.some !== null) {
      this.some.hidden = shengci.length === 0;
    }

    if (this.none !== null) {
      this.none.hidden = shengci.length > 0;
    }

    this.mark();

    const body = this.list?.closest<HTMLElement>(".yuedu-body");

    if (body !== null && body !== undefined) {
      holdRows(body);
    }

    for (const passage of this.passages) {
      passage.dataset["cbShengci"] = "marked";
    }

    if (this.section !== null) {
      this.section.hidden = false;
    }

    return this.list;
  }

  /** Whether the reader holds the word, as far as this page knows. */
  holds(word: string): boolean {
    return this.held.has(word);
  }

  /**
   * Take a press that saved or removed a word, wherever it was made.
   * @param changed - The word, and whether the reader now holds it.
   * @param root - Where else a control for it may be, the popover among them.
   */
  change(changed: { word: string; held: boolean }, root: ParentNode): void {
    if (changed.held) {
      this.held.add(changed.word);
    } else {
      this.held.delete(changed.word);
    }

    this.mark();

    for (const control of root.querySelectorAll<HTMLElement>(".cb-save-word")) {
      const button = buttonOf(control);

      if (button?.value === changed.word) {
        drawSavedWord(button, changed.held);
      }
    }
  }

  /** Put the mark on every word in the text the reader does not hold. */
  private mark(): void {
    for (const passage of this.passages) {
      for (const word of passage.querySelectorAll<HTMLElement>(wordSelector)) {
        word.classList.toggle(
          shengciClass,
          !this.held.has(word.dataset["cbWord"] ?? ""),
        );
      }
    }
  }
}
